import { Phone, ArrowRight, CheckCircle2, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { companyInfo } from '../data/pricing';
import { useState } from 'react';
import { PlanUploadModal } from '../components/PlanUploadModal';

export const Extensions = () => {
  const [showPlanUpload, setShowPlanUpload] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SEO 
        title="Extension Electrical Installation London | Crescent Electrical"
        description="Professional electrical installation for home extensions across London. From planning to certification. Work seamlessly with your builder. Get your project quote."
        canonical="https://crescentelectrical.co.uk/extensions"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
            EXTENSION ELECTRICAL
            <span className="block text-[#0066FF] mt-2">INSTALLATIONS</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl">
            Get the electrical installation planned before the walls close. 
            We coordinate seamlessly with your main contractor, architect, and interior designer.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
            >
              Get A Project Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowPlanUpload(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B132B]"
            >
              <Upload className="mr-2 h-5 w-5" />
              Upload Your Plans
            </Button>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#0F172A] mb-6">
                BUILDING AN EXTENSION OR ADDING SPACE?
              </h2>
              <p className="text-lg text-[#475569] mb-6">
                Whether you're adding a rear extension, side return, loft conversion or conservatory, 
                Crescent Electrical delivers the complete electrical package from design through to certification.
              </p>
              <p className="text-lg text-[#475569] mb-8">
                We work directly with your builder or main contractor to ensure electrical installation 
                is coordinated at every stage — from initial planning through first fix, second fix, 
                testing and final handover.
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1601462904263-f2fa0c851cb9?w=800&q=85"
                alt="Extension electrical work"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Included */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#0F172A] text-center mb-12">
            Extension Electrical Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Electrical design and layout planning',
              'New circuit distribution',
              'Consumer unit upgrades',
              'First fix cable installation',
              'Second fix fittings',
              'New socket circuits',
              'Lighting circuits and controls',
              'Kitchen and appliance supplies',
              'Bathroom electrical zones',
              'Underfloor heating controls',
              'Smart home preparation',
              'Data and networking',
              'EV charger preparation',
              'Garden power and lighting',
              'Testing and certification',
              'Building Control notification'
            ].map((service, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-[#0066FF] flex-shrink-0 mt-0.5" />
                <span className="text-[#475569] font-medium">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-[#0F172A] text-center mb-12">
            How We Work With Your Extension Project
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'PLANNING STAGE',
                description: 'Review your architect drawings and create electrical layout plan'
              },
              {
                step: '02',
                title: 'FIRST FIX',
                description: 'Install cables, boxes and back plates before walls are plastered'
              },
              {
                step: '03',
                title: 'SECOND FIX & TEST',
                description: 'Fit all accessories, test circuits and issue NICEIC certification'
              }
            ].map((phase) => (
              <div key={phase.step} className="bg-[#F8F9FA] rounded-xl p-8 border-2 border-gray-200">
                <div className="w-12 h-12 bg-[#0066FF] text-white rounded-xl flex items-center justify-center font-mono font-bold mb-4">
                  {phase.step}
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{phase.title}</h3>
                <p className="text-[#475569]">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0B132B] to-[#1C2541] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Discuss Your Extension?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Send us your plans or give us a call to discuss your electrical requirements
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
            >
              Get A Quote
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => setShowPlanUpload(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0B132B]"
            >
              Upload Plans
            </Button>
          </div>
          <a 
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-2 text-[#0066FF] hover:text-[#F59E0B] font-semibold text-lg"
          >
            <Phone className="h-5 w-5" />
            {companyInfo.phone}
          </a>
        </div>
      </section>

      <PlanUploadModal isOpen={showPlanUpload} onClose={() => setShowPlanUpload(false)} />
    </div>
  );
};